import React, { useState, useEffect, useRef, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ROUTE_PATH } from '../../routes/route-paths';
import { PackageDetailsResponse, useClientCode, VerticalKeys } from '../../hooks/api/useClientCode';
import useAuth from '../../hooks/utils/useAuth';
import { useSessionStorage } from '../../hooks/utils/useSessionStorage';
import { InlineSpinner } from '../../components/common/spinner';
import { useDefaultSettings } from '../../hooks/api/useDefaultSettings';
import Slide1 from './images/descriptionSlide/Slide1.webp';
import { usePeersonaFeatureFlagEnabledForClient } from '../../hooks/api/useFeatureFlag';
import healthcareSlide3 from './images/verticals/healthcareSlide3.webp';
import educationSlide1 from './images/verticals/educationSlide1.webp';
import employersSlide1 from './images/verticals/employersSlide1.webp';
import Checkmark from './images/checkmark.svg';
import { PackageDetails } from '../../hooks/api/usePackageDetails';
import { domainLogo, domainLogoAlt, domainLogoWhite } from '../../components/domain-logo';
import { faithSlide1, faithSlide3, faithSlide2 } from './images/verticals';
import { resolveTheme } from '../../themes';
import { THEME_ID } from '../../themes/types';
import {
  getBenefitsIntroCopy,
  getLandingDescriptionCopy,
  getProductWalkthroughCopy,
  type BenefitsIntroCopy,
  type LandingDescriptionCopy,
} from './common/copy';
// Lazy load ProductSlides components - they're below the fold and import heavy video assets
const ProductSlidesDesktop = lazy(() => import('./components/desktop/product-slides-desktop'));
const ProductSlidesMobile = lazy(() => import('./components/product-slides-mobile'));

// Map verticals to their image file names (constant, defined outside component)
const VERTICAL_IMAGE_MAP: Record<VerticalKeys, string[]> = {
  [VerticalKeys.HIGHER_EDUCATION]: ['educationSlide1', 'educationSlide2', 'educationSlide3'],
  [VerticalKeys.EMPLOYER]: ['employersSlide1', 'employersSlide2', 'employersSlide3'],
  [VerticalKeys.SENIOR]: ['seniorsSlide1', 'seniorsSlide2', 'seniorsSlide3'],
  [VerticalKeys.HEALTHCARE]: ['healthcareSlide1', 'healthcareSlide2', 'healthcareSlide3'],
  [VerticalKeys.FAITH_BASED]: ['faithSlide1', 'faithSlide2', 'faithSlide3'],
};

const WelcomePageNew2 = (): JSX.Element => {
  const [showHeader, setShowHeader] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const ctaRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const clientCode = useSessionStorage('clientCode');

  const {
    data: packageDetails,
    isLoading: clientIsLoading,
    fetchStatus,
  } = useClientCode({
    partnerCode: clientCode || undefined,
  });
  const peersonaFeatureFlags = usePeersonaFeatureFlagEnabledForClient();
  const { data: defaultSettingData, isLoading: defaultDatatIsLoading } = useDefaultSettings();
  const landingPageLogo = packageDetails?.client?.logo_file_url;
  const landingPageMessage =
    packageDetails?.client?.landing_page_content || defaultSettingData?.defaultLandingPageMessage;

  const { user, keycloak, redirectTo } = useAuth();

  const isSSO = user?.caller_role.active_subscription?.package.client.sso_configuration?.collect_password === false;

  const getStartedLink = isSSO ? ROUTE_PATH.ONBOARDING : ROUTE_PATH.SIGNUP;

  // == CTA Images and Carousel ==
  const vertical = packageDetails?.client?.vertical?.key;
  const theme = resolveTheme();
  const landingDescriptionCopy = getLandingDescriptionCopy(theme.id);
  const productWalkthroughCopy = getProductWalkthroughCopy(theme.id);
  const benefitsIntroCopy = getBenefitsIntroCopy(theme.id);
  // if we're on the kindlychristian domain, use the faith images, otherwise use the default images
  const defaultImages =
    theme.id === THEME_ID.KINDLY_CHRISTIAN
      ? [faithSlide1, faithSlide2, faithSlide3]
      : [healthcareSlide3, educationSlide1, employersSlide1];
  const [ctaImages, setCtaImages] = useState<string[]>(defaultImages);

  // Dynamically load images only for the current vertical
  useEffect(() => {
    // we disregard vertical if we're on the kindlychristian domain
    if (!vertical || !VERTICAL_IMAGE_MAP[vertical] || theme.id === THEME_ID.KINDLY_CHRISTIAN) {
      return;
    }

    const loadVerticalImages = async () => {
      try {
        const imageNames = VERTICAL_IMAGE_MAP[vertical];
        const imageImports = imageNames.map((name) => import(`./images/verticals/${name}.webp`).then((m) => m.default));
        const images = await Promise.all(imageImports);
        setCtaImages(images);
      } catch (error) {
        console.error('Failed to load vertical images:', error);
        // Fallback to default images on error
        setCtaImages([healthcareSlide3, educationSlide1, employersSlide1]);
      }
    };

    loadVerticalImages();
  }, [vertical, theme.id]);

  // Preload images to prevent gradient-only flash
  useEffect(() => {
    if (ctaImages.length > 0) {
      let loadedCount = 0;
      const totalImages = ctaImages.length;

      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
        }
      };

      ctaImages.forEach((imageSrc) => {
        const img = new Image();
        img.onload = checkAllLoaded;
        img.onerror = checkAllLoaded; // Count as loaded even if error to prevent infinite waiting
        img.src = imageSrc;
      });
    }
  }, [ctaImages]);

  // Cycle through the images every 4 seconds
  useEffect(() => {
    if (ctaImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % ctaImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [ctaImages]);

  // Screen size detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Scroll detection for header fade-in
  useEffect(() => {
    const onScroll = () => setShowHeader((window.scrollY || 0) > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const renderDesktopLayout = () => (
    <div className="relative">
      <Header
        showHeader={showHeader}
        isMobile={false}
        logo={landingPageLogo}
        keycloak={keycloak}
        redirectTo={redirectTo}
        getStartedLink={getStartedLink}
      />
      {clientCode && landingPageLogo && (
        <ClientHeader
          logo={landingPageLogo}
          message={landingPageMessage}
          clientName={packageDetails?.client?.name}
          isMobile={false}
        />
      )}
      <CTASection
        images={ctaImages}
        currentImageIndex={currentImageIndex}
        ctaRef={ctaRef}
        isMobile={false}
        keycloak={keycloak}
        redirectTo={redirectTo}
        getStartedLink={getStartedLink}
      />
      <DescriptionSection descriptionRef={descriptionRef} descriptionCopy={landingDescriptionCopy} isMobile={false} />
      <ProductSlidesDesktop
        hasPeersona={peersonaFeatureFlags}
        productWalkthroughCopy={productWalkthroughCopy}
        theme={theme}
      />
      {clientCode && (
        <BenefitsSection
          isMobile={false}
          benefitsIntroCopy={benefitsIntroCopy}
          packageDetails={packageDetails}
          peersonasFeatureFlags={peersonaFeatureFlags}
        />
      )}
      <TestimonialsSection isMobile={false} />
      <Footer isMobile={false} />
    </div>
  );

  const renderMobileLayout = () => (
    <div className="min-h-screen overflow-y-scroll scroll-smooth" style={{ minHeight: '100dvh' }}>
      <Header
        showHeader={showHeader}
        isMobile={true}
        logo={landingPageLogo}
        keycloak={keycloak}
        redirectTo={redirectTo}
        getStartedLink={getStartedLink}
      />
      {clientCode && landingPageLogo && (
        <ClientHeader
          logo={landingPageLogo}
          message={landingPageMessage}
          clientName={packageDetails?.client?.name}
          isMobile={true}
        />
      )}
      <CTASection
        images={ctaImages}
        currentImageIndex={currentImageIndex}
        ctaRef={ctaRef}
        isMobile={true}
        keycloak={keycloak}
        redirectTo={redirectTo}
        getStartedLink={getStartedLink}
      />
      <DescriptionSection descriptionRef={descriptionRef} descriptionCopy={landingDescriptionCopy} isMobile={true} />
      <ProductSlidesMobile
        hasPeersona={peersonaFeatureFlags}
        productWalkthroughCopy={productWalkthroughCopy}
        themeId={theme.id}
      />
      {clientCode && (
        <BenefitsSection
          isMobile={true}
          benefitsIntroCopy={benefitsIntroCopy}
          packageDetails={packageDetails}
          peersonasFeatureFlags={peersonaFeatureFlags}
        />
      )}
      <TestimonialsSection isMobile={true} />
      <Footer isMobile={true} />
    </div>
  );

  return (
    <>
      {(clientIsLoading && clientCode) || defaultDatatIsLoading || fetchStatus !== 'idle' || !imagesLoaded ? (
        <div className="flex h-screen items-center justify-center">
          <InlineSpinner />
        </div>
      ) : isDesktop ? (
        renderDesktopLayout()
      ) : (
        renderMobileLayout()
      )}
    </>
  );
};

// Shared CTA Button Component
const CTAButton: React.FC<{
  type: 'login' | 'getStarted';
  keycloak?: any;
  redirectTo?: string | null;
  getStartedLink?: string;
  isMobile?: boolean;
}> = ({ type, keycloak, redirectTo, getStartedLink, isMobile = false }) => {
  const isLogin = type === 'login';
  const buttonClass = isLogin
    ? 'inline-flex h-12 items-center justify-center gap-2.5 rounded-full bg-landing-cream px-5 py-2.5 hover:bg-gray-50'
    : 'inline-flex h-12 items-center justify-center gap-2.5 rounded-full border border-landing-border/30 bg-section-header-slate px-5 py-2.5 hover:bg-slate-hover';

  const textClass = isLogin
    ? 'justify-start text-base font-normal text-black'
    : 'justify-start text-base font-semibold text-landing-border';

  const widthClass = isMobile ? 'w-48' : 'w-64';
  const textSizeClass = isMobile ? 'text-sm' : 'text-base';

  if (isLogin) {
    return (
      <button
        onClick={() =>
          keycloak?.login({
            redirectUri: `${window.location.origin}${redirectTo || ''}`,
          })
        }
        className={`${buttonClass} ${widthClass} cursor-pointer`}
      >
        <div className={`${textClass} ${textSizeClass}`}>Log In</div>
      </button>
    );
  }

  return (
    <a href={getStartedLink} className={`${buttonClass} ${widthClass} cursor-pointer`}>
      <div className={`${textClass} ${textSizeClass}`}>Get Started</div>
    </a>
  );
};

const Header: React.FC<{
  showHeader: boolean;
  isMobile?: boolean;
  logo?: string;
  keycloak?: any;
  redirectTo?: string | null;
  getStartedLink?: string;
}> = ({ showHeader, isMobile = false, logo, keycloak, redirectTo, getStartedLink }) => {
  if (isMobile) {
    // Mobile: Fixed bottom footer - always visible below sm breakpoint
    return (
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 w-full px-8 py-4 transition-all duration-300 ${
          showHeader
            ? 'bg-white opacity-100 shadow-lg'
            : 'bg-white opacity-100 shadow-lg sm:bg-transparent sm:opacity-0'
        }`}
      >
        <img
          src={domainLogo}
          alt={domainLogoAlt}
          className="mx-auto mb-3 flex h-6 items-center justify-center pl-3 sm:hidden"
        />
        <div className="pointer-events-auto flex items-center justify-center gap-4">
          <CTAButton type="login" keycloak={keycloak} redirectTo={redirectTo} isMobile={true} />
          <CTAButton type="getStarted" getStartedLink={getStartedLink} isMobile={true} />
        </div>
      </div>
    );
  }

  // Desktop: Fixed top header
  return (
    <div
      className={`fixed left-0 right-0 top-0 z-50 h-20 w-full px-12 transition-all duration-300 ${
        showHeader ? 'bg-white opacity-100' : 'bg-transparent opacity-0'
      }`}
    >
      <div className="pointer-events-auto flex h-full items-center justify-between">
        <img src={domainLogo} alt={`${domainLogoAlt} logo`} className="h-8 w-auto" />
        <div className="pointer-events-auto flex items-center gap-4">
          <CTAButton type="login" keycloak={keycloak} redirectTo={redirectTo} isMobile={false} />
          <CTAButton type="getStarted" getStartedLink={getStartedLink} isMobile={false} />
        </div>
      </div>
    </div>
  );
};

const ClientHeader: React.FC<{ logo?: string; message?: string; clientName?: string; isMobile?: boolean }> = ({
  logo,
  message,
  clientName,
  isMobile = false,
}) => {
  const theme = resolveTheme();
  if (isMobile) {
    return (
      <div className="flex w-full flex-col items-center justify-center bg-white px-8 py-8">
        <div className="flex w-full max-w-4xl flex-col gap-8">
          {logo && (
            <img src={logo} alt={`${clientName ?? theme.name} logo`} className="w-auto max-w-sm object-contain" />
          )}
          {message && (
            <div
              className="text-start text-base font-normal leading-6 text-black sm:text-lg sm:leading-7"
              dangerouslySetInnerHTML={{ __html: message }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-44 w-full items-center justify-start gap-24 px-24 py-12">
      {logo && <img src={logo} alt={`${clientName ?? theme.name} logo`} className="w-auto max-w-sm flex-shrink-0" />}
      {message && (
        <div
          className="max-w-200 text-wrap text-lg font-normal leading-7 text-black"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      )}
    </div>
  );
};

const CTASection: React.FC<{
  images: string[];
  currentImageIndex: number;
  ctaRef: React.RefObject<HTMLDivElement>;
  isMobile?: boolean;
  keycloak?: any;
  redirectTo?: string | null;
  getStartedLink?: string;
}> = ({ images, currentImageIndex, ctaRef, isMobile = false, keycloak, redirectTo, getStartedLink }) => {
  if (isMobile) {
    return (
      <div
        ref={ctaRef}
        className="relative flex h-60 w-full select-none overflow-hidden bg-cover bg-top bg-no-repeat px-8 py-6 sm:h-80 sm:py-8"
      >
        {images.map((image, index) => (
          <div
            key={index}
            className={`duration-2000 pointer-events-none absolute inset-0 bg-cover bg-right-top bg-no-repeat transition-opacity sm:bg-center ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0)), url(${image})`,
            }}
          />
        ))}
        <div className="z-200 pointer-events-auto relative hidden h-full w-full flex-col items-start justify-between sm:flex">
          <div className="flex flex-col items-start sm:gap-4">
            <img src={domainLogoWhite} alt={`${domainLogoAlt} logo`} className="h-12 w-auto sm:h-16" />
            <div className="text-left font-['Manrope'] text-lg font-normal leading-6 text-white sm:text-2xl sm:leading-8 lg:text-3xl lg:leading-10">
              Wellbeing, powered by connection
            </div>
          </div>
          <div className="z-200 hidden flex-col items-start gap-4 sm:flex sm:flex-row sm:items-start">
            <CTAButton type="login" keycloak={keycloak} redirectTo={redirectTo} isMobile={true} />
            <CTAButton type="getStarted" getStartedLink={getStartedLink} isMobile={true} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ctaRef}
      className="h-124 relative flex w-full select-none overflow-hidden bg-cover bg-top bg-no-repeat px-24 py-24"
    >
      {images.map((image, index) => (
        <div
          key={index}
          className={`duration-2000 pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0)), url(${image})`,
          }}
        />
      ))}
      <div className="relative z-10 flex w-full flex-col items-start justify-between">
        <div className="flex flex-col gap-6">
          <img src={domainLogoWhite} alt={`${domainLogoAlt} logo`} className="h-12 w-auto sm:h-16" />
          <div className="w-90 leading-12 justify-start font-['Manrope'] text-4xl font-normal text-white">
            Wellbeing, powered by connection
          </div>
        </div>
        <div className="relative z-20 flex items-center gap-4">
          <CTAButton type="login" keycloak={keycloak} redirectTo={redirectTo} isMobile={false} />
          <CTAButton type="getStarted" getStartedLink={getStartedLink} isMobile={false} />
        </div>
      </div>
    </div>
  );
};

const renderLandingDescriptionBody = (paragraphs: readonly string[]) =>
  paragraphs.map((paragraph, index) => (
    <React.Fragment key={index}>
      {index > 0 && (
        <>
          <br />
          <br />
        </>
      )}
      {paragraph}
    </React.Fragment>
  ));

const DescriptionSection: React.FC<{
  descriptionRef: React.RefObject<HTMLDivElement>;
  descriptionCopy: LandingDescriptionCopy;
  isMobile?: boolean;
}> = ({ descriptionRef, descriptionCopy, isMobile = false }) => {
  if (isMobile) {
    return (
      <div
        ref={descriptionRef}
        data-description-section
        className="bg-landing-cream flex w-full select-none flex-col items-center justify-center px-8 py-10"
      >
        <div className="flex w-full max-w-4xl flex-col gap-8">
          <div className="justify-start font-['Manrope'] text-2xl font-light text-black sm:text-[28px]">
            {descriptionCopy.eyebrow}
          </div>
          <div className="text-12 justify-center font-['Manrope'] font-light leading-[52px] text-black sm:text-[64px] sm:leading-[72px]">
            {descriptionCopy.headline}
          </div>
          <div className="justify-start font-['Manrope'] text-lg font-normal leading-7 text-black">
            {renderLandingDescriptionBody(descriptionCopy.bodyParagraphs)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={descriptionRef}
      data-description-section
      className="bg-landing-cream flex h-[880px] w-full select-none items-center gap-28 py-24 pl-24"
    >
      <div className="flex w-2/5 flex-col gap-8 lg:gap-12">
        <div className="flex flex-col lg:gap-4">
          <div className="justify-start font-['Manrope'] text-[28px] font-light text-black">
            {descriptionCopy.eyebrow}
          </div>
          <div className="text-12 w-full justify-start font-['Manrope'] font-light leading-[72px] text-black xl:text-[64px]">
            {descriptionCopy.headline}
          </div>
        </div>
        <div className="w-full justify-start font-['Manrope'] text-lg font-light leading-7 text-black">
          {renderLandingDescriptionBody(descriptionCopy.bodyParagraphs)}
        </div>
      </div>
      <img
        src={Slide1}
        alt={descriptionCopy.illustrationAlt}
        className="min-w-100 mx-8 w-1/2 max-w-xl rounded-lg object-cover"
      />
    </div>
  );
};

const BenefitsSection: React.FC<{
  isMobile?: boolean;
  benefitsIntroCopy: BenefitsIntroCopy;
  packageDetails?: PackageDetails | PackageDetailsResponse;
  peersonasFeatureFlags?: boolean;
}> = ({ isMobile = false, benefitsIntroCopy, packageDetails, peersonasFeatureFlags }) => {
  const hasUnlimitedAccess = packageDetails?.package_products.some(
    (product) => product.product.key === 'UNLIMITED_ACCESS' && product.status === 'enabled',
  );

  const hasCappedAccess = packageDetails?.package_products.some((product) => product.product.key === 'CAPPED_ACCESS');
  const cappedAccessProduct = packageDetails?.package_products.find(
    (product) => product.product.key === 'CAPPED_ACCESS',
  );
  const cappedMinutes = cappedAccessProduct?.configuration?.maximum_minutes_per_user;
  const cappedFrequency =
    cappedAccessProduct?.configuration?.auto_renew_frequency === 'yearly' ? 'Per Year' : 'Per Month';

  const hasDependentsAccess = packageDetails?.dependents_configuration?.enabled;

  const hasResources =
    packageDetails?.client?.use_global_resources || packageDetails?.client?.enabled_resource_ids?.length;

  const hasPeersonasAccess = peersonasFeatureFlags;

  const hasVirtualCare = packageDetails?.external_plan_id;

  const Description = () => (
    <>
      <div className="leading-12 font-manrope justify-start text-4xl font-normal text-black">
        {benefitsIntroCopy.headline}
      </div>
      <div className="justify-start font-['Montserrat'] text-lg font-semibold leading-7 text-black">
        {benefitsIntroCopy.bodyParagraphs[0]}
        <br /> <br />
        {benefitsIntroCopy.bodyParagraphs[1]}
      </div>
    </>
  );

  const Benefits = () => (
    <>
      <div className="bg-connect-bg/30 flex w-96 flex-col items-center justify-start gap-4 rounded-[10px] py-7 pb-12">
        <div className="w-4/5 border-b border-blue-950 pb-2 font-['Manrope'] text-2xl font-bold uppercase leading-9 text-blue-950">
          Your Benefits
        </div>
        <div className="flex w-4/5 flex-col items-start justify-center space-y-4">
          {hasUnlimitedAccess && (
            <div className="text-md text-primary-dark flex items-center justify-start gap-2 pl-4 font-['Montserrat'] leading-6">
              <img src={Checkmark} className="w-7" alt="Benefit Graphic" /> <p>Unlimited Talk Time</p>
            </div>
          )}
          {hasCappedAccess && (
            <div className="text-md text-primary-dark flex items-center justify-start gap-2 pl-4 font-['Montserrat'] leading-6">
              <img src={Checkmark} className="w-7" alt="Benefit Graphic" />{' '}
              <p>
                {cappedMinutes} Minutes of Talk Time {cappedFrequency}
              </p>
            </div>
          )}
          <div className="text-md text-primary-dark flex items-center justify-start gap-2 pl-4 font-['Montserrat'] leading-6">
            <img src={Checkmark} className="w-7" alt="Benefit Graphic" /> <p>Unlimited Experience Plays</p>
          </div>
          {hasDependentsAccess && (
            <div className="text-md text-primary-dark flex items-center justify-start gap-2 pl-4 font-['Montserrat'] leading-6">
              <img src={Checkmark} className="w-7" alt="Benefit Graphic" /> <p>Dependant Access</p>
            </div>
          )}
          {hasResources && (
            <div className="text-md text-primary-dark flex items-center justify-start gap-2 pl-4 font-['Montserrat'] leading-6">
              <img src={Checkmark} className="w-7" alt="Benefit Graphic" /> <p>Resources</p>
            </div>
          )}
          {hasPeersonasAccess && (
            <div className="text-md text-primary-dark flex items-center justify-start gap-2 pl-4 font-['Montserrat'] leading-6">
              <img src={Checkmark} className="w-7" alt="Benefit Graphic" /> <p>Unlimited Peersonas AI Chats</p>
            </div>
          )}
          {hasVirtualCare && (
            <div className="text-md text-primary-dark flex items-center justify-start gap-2 pl-4 font-['Montserrat'] leading-6">
              <img src={Checkmark} className="w-7" alt="Benefit Graphic" /> <p>Virtual Care Services</p>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div className="bg-landing-off-white flex w-full select-none flex-col items-start justify-start px-8 py-10">
        <div className="flex w-full flex-col gap-8">
          <Description />
          <Benefits />
        </div>
      </div>
    );
  }

  return (
    <div className="px-30 bg-landing-off-white flex w-full select-none items-start justify-between py-20">
      <div className="flex w-1/2 flex-col gap-8">
        <Description />
      </div>
      <div className="flex w-1/2 items-center justify-end">
        <Benefits />
      </div>
    </div>
  );
};

// Shared Testimonials Data and Logic
const useTestimonials = () => {
  const testimonials: React.ReactNode[] = [
    <div className="font-['Montserrat'] text-base font-normal leading-6 text-black sm:text-lg sm:leading-7">
      "<span className="font-semibold">My Peer was amazing.</span> I figured I would give the service a try. She has
      great rapport. I felt very comfortable talking to her and she was very patient. She brought the right energy and
      caring."
    </div>,
    <div className="font-['Montserrat'] text-base font-normal leading-6 text-black sm:text-lg sm:leading-7">
      "Erin is my go to listener. She has an{' '}
      <span className="font-semibold">excellent ability to hold space and give me room to talk.</span> These are the
      most important qualities to me. She is the reason I keep coming back."
    </div>,
    <div className="font-['Montserrat'] text-base font-normal leading-6 text-black sm:text-lg sm:leading-7">
      "<span className="font-semibold">Amazing service that I am fortunate to receive as a benefit</span> as a Door Dash
      driver. It's always nice to just have a friendly chat over personal experiences and matters with an honest and
      caring individual!"
    </div>,
  ];

  const [testimonialIndex, setTestimonialIndex] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  useEffect(() => {
    const id = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setTestimonialIndex((i) => (i + 1) % testimonials.length);
        setIsTransitioning(false);
      }, 300); // Half of the transition duration
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const handleDotClick = (index: number) => {
    if (index === testimonialIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setTestimonialIndex(index);
      setIsTransitioning(false);
    }, 300);
  };

  const Dot = ({ index }: { index: number }) => (
    <button
      aria-label={`Go to testimonial ${index + 1}`}
      onClick={() => handleDotClick(index)}
      className={
        `h-2 w-2 rounded-full transition-colors duration-300 ` +
        (testimonialIndex === index ? 'bg-gray-500' : 'bg-gray-300 hover:bg-gray-400')
      }
    />
  );

  return { testimonials, testimonialIndex, isTransitioning, Dot };
};

const TestimonialsSection: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const { testimonials, testimonialIndex, isTransitioning, Dot } = useTestimonials();

  if (isMobile) {
    return (
      <div className="bg-landing-cream flex w-full select-none flex-col items-center justify-between px-8 py-20">
        <div className="flex w-4/5 flex-col items-center gap-8 text-center">
          <div
            className={`duration-600 transition-opacity ease-in-out ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
          >
            {testimonials[testimonialIndex]}
          </div>
          <div className="mt-4 flex items-center gap-2">
            {testimonials.map((_, i) => (
              <Dot key={i} index={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-landing-cream flex w-full select-none items-center justify-center py-16 pl-24">
      <div className="flex w-full max-w-3xl flex-col items-center gap-8 text-center">
        <div
          className={`duration-600 font-['Manrope'] text-[28px] font-normal leading-9 text-black transition-opacity ease-in-out ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {testimonials[testimonialIndex]}
        </div>
        <div className="mt-2 flex items-center gap-2">
          {testimonials.map((_, i) => (
            <Dot key={i} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

const Footer: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  if (isMobile) {
    return (
      <div className="flex w-full flex-col items-center justify-between bg-white px-8 py-6">
        {/* FAQ LINK AND SUPPORT LINK */}
        <div className="flex flex-col items-center justify-center gap-4 text-center font-['Montserrat'] text-base font-normal leading-6 text-black">
          <Link to={ROUTE_PATH.FAQS}>FAQ</Link>
          <a href="mailto:questions@kindlyhuman.com">Support</a>
        </div>
        {/* KINDLY HUMAN LOGO */}
        <img src={domainLogo} alt={`${domainLogoAlt} logo`} className="mt-8 hidden h-10 flex-shrink-0 pl-5 sm:block" />
        <div className="h-12 sm:hidden" />
      </div>
    );
  }

  return (
    <div className="flex h-auto w-full items-end justify-between bg-white px-28 pb-12 pt-24">
      <div className="flex flex-col gap-4 pt-12 font-['Montserrat'] text-lg font-normal leading-7 text-black">
        <Link to={ROUTE_PATH.FAQS}>FAQ</Link>
        <a href="mailto:questions@kindlyhuman.com">Support</a>
      </div>
      <img src={domainLogo} alt={`${domainLogoAlt} logo`} className="h-16 flex-shrink-0" />
    </div>
  );
};

export default WelcomePageNew2;
